import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight, X, Gift, Shield, Upload, ImageIcon, Loader2 } from 'lucide-react';
import ScopeSelector from '../components/ScopeSelector';
import { PageTooltip } from '../components/TooltipSystem';

const EMOJIS = ['🌮', '🍟', '🍸', '🥑', '🌶️', '🌯', '🍺', '💰', '💵'];
const TIERS = ['bronze', 'silver', 'gold', 'platinum'];
const DAYS = [
  { value: 0, label: 'Lun' }, { value: 1, label: 'Mar' }, { value: 2, label: 'Mer' },
  { value: 3, label: 'Gio' }, { value: 4, label: 'Ven' }, { value: 5, label: 'Sab' }, { value: 6, label: 'Dom' },
];
const PERIODS = [
  { value: 'week', label: 'Settimana' },
  { value: 'month', label: 'Mese' },
  { value: 'year', label: 'Anno' },
];

const TABS = [
  { id: 'rewards', label: 'Premi a Punti', icon: Gift },
  { id: 'benefits', label: 'Vantaggi Tier', icon: Shield },
];

const EMPTY_REWARD = { name: '', description: '', emoji: '🌮', image_url: '', terms: '', points_cost: '', min_tier: 'bronze', available_from_hour: '', available_to_hour: '', daily_limit: '', allowed_days: [], is_active: true };
const EMPTY_BENEFIT = { tier: 'silver', benefit_type: 'discount', discount_pct: '', item_name: '', item_emoji: '🌮', freq_count: '1', freq_period: 'week', allowed_days: [], description: '', is_active: true };

export default function AdminPremi() {
  const [tab, setTab] = useState('rewards');

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="admin-premi-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">Premi</h1>
        <p className="text-[#64748B] text-sm mt-1">Gestisci premi a punti e vantaggi tier</p>
      </div>
      {/* Tab bar */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl mb-6 overflow-x-auto" data-testid="premi-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              tab === t.id ? 'bg-white text-[#D44E20] shadow-sm' : 'text-[#64748B] hover:text-[#1A1A1A]'
            }`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'rewards' && <RewardsTab />}
      {tab === 'benefits' && <BenefitsTab />}
    </div>
  );
}

/* ── REWARDS TAB ──────────────────────────────────────────────── */
function RewardsTab() {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_REWARD);
  const [scope, setScope] = useState('brand');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetch = () => { adminApi.get('/rewards').then(r => setRewards(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { fetch(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY_REWARD); setScope('brand'); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r.id);
    setForm({
      name: r.name, description: r.description || '', emoji: r.emoji,
      image_url: r.image_url || '', terms: r.terms || '',
      points_cost: r.points_cost, min_tier: r.min_tier,
      available_from_hour: r.available_from_hour ?? '', available_to_hour: r.available_to_hour ?? '',
      daily_limit: r.daily_limit ?? '', allowed_days: r.allowed_days || [], is_active: r.is_active,
    });
    setScope(r.scope || 'brand');
    setShowForm(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Immagine troppo grande (max 10MB)'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Seleziona un file immagine'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await adminApi.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, image_url: res.data.url }));
      toast.success('Immagine caricata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const toggleDay = (d) => {
    setForm(f => ({ ...f, allowed_days: f.allowed_days.includes(d) ? f.allowed_days.filter(x => x !== d) : [...f.allowed_days, d] }));
  };

  const handleSave = async () => {
    if (!form.name || !form.points_cost) { toast.error('Nome e costo punti obbligatori'); return; }
    setSaving(true);
    const payload = {
      name: form.name, description: form.description, emoji: form.emoji,
      image_url: form.image_url || null,
      terms: form.terms || null,
      points_cost: parseInt(form.points_cost), min_tier: form.min_tier,
      available_from_hour: form.available_from_hour !== '' ? parseInt(form.available_from_hour) : null,
      available_to_hour: form.available_to_hour !== '' ? parseInt(form.available_to_hour) : null,
      daily_limit: form.daily_limit !== '' ? parseInt(form.daily_limit) : null,
      allowed_days: form.allowed_days.length > 0 ? form.allowed_days : null,
      is_active: form.is_active,
      scope: scope,
    };
    try {
      if (editing) { await adminApi.put(`/rewards/${editing}`, payload); toast.success('Premio aggiornato'); }
      else { await adminApi.post('/rewards', payload); toast.success('Premio creato'); }
      setShowForm(false); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (r) => { await adminApi.put(`/rewards/${r.id}`, { is_active: !r.is_active }); fetch(); };
  const handleDelete = async (id) => { if (!window.confirm('Eliminare questo premio?')) return; await adminApi.delete(`/rewards/${id}`); toast.success('Premio eliminato'); fetch(); };

  const dayLabel = (days) => {
    if (!days || days.length === 0 || days.length === 7) return null;
    return days.sort((a, b) => a - b).map(d => DAYS.find(x => x.value === d)?.label).join(', ');
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-[#64748B] text-sm">{rewards.length} premi configurati</p>
        <button onClick={openAdd} data-testid="add-reward-btn" className="flex items-center gap-2 px-4 py-2.5 bg-[#D44E20] text-white rounded-xl text-sm font-semibold hover:bg-[#B83D15] transition-colors">
          <Plus size={16} /> Aggiungi Premio
        </button>
      </div>
      {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div> : (
        <div className="space-y-3">
          {rewards.map(r => (
            <div key={r.id} data-testid={`reward-item-${r.id}`} className={`bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex items-center gap-4 ${!r.is_active ? 'opacity-50' : ''}`}>
              {r.image_url ? (
                <img src={r.image_url} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
              ) : (
                <span className="text-3xl">{r.emoji}</span>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-[#1A1A1A] font-semibold text-sm">{r.name}</p>
                  <span className="text-xs text-[#64748B] capitalize border border-slate-200 px-1.5 py-0.5 rounded-full">{r.min_tier}</span>
                </div>
                <p className="text-[#64748B] text-xs">{r.description}</p>
                <div className="flex gap-2 mt-1 flex-wrap">
                  <span className="text-[#D44E20] text-xs font-bold">{r.points_cost} pt</span>
                  {r.available_from_hour != null && <span className="text-[#64748B] text-xs">{r.available_from_hour}:00-{r.available_to_hour}:00</span>}
                  {r.daily_limit && <span className="text-[#64748B] text-xs">Max {r.daily_limit}/giorno</span>}
                  {dayLabel(r.allowed_days) && <span className="text-blue-600 text-xs bg-blue-50 px-1.5 py-0.5 rounded-full">{dayLabel(r.allowed_days)}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleToggle(r)} className="text-[#64748B] hover:text-[#D44E20]">
                  {r.is_active ? <ToggleRight size={22} className="text-[#3ECF8E]" /> : <ToggleLeft size={22} />}
                </button>
                <button onClick={() => openEdit(r)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-50 text-[#64748B] hover:bg-slate-100"><Pencil size={14} /></button>
                <button onClick={() => handleDelete(r.id)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-[#EF4444] hover:bg-red-100"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Reward Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-[#1A1A1A] text-lg">{editing ? 'Modifica Premio' : 'Nuovo Premio'}</h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-[#64748B] hover:bg-gray-200"><X size={16} /></button>
            </div>
            {/* Image upload — replaces emoji on customer cards when set.
                Falls back to emoji when not provided. */}
            <div className="mb-4">
              <label className="text-[#64748B] text-xs font-medium block mb-1.5">Immagine premio (opzionale, consigliata)</label>
              <div className="flex items-center gap-3">
                <div className="w-20 h-20 rounded-xl overflow-hidden border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center flex-shrink-0">
                  {form.image_url ? (
                    <img src={form.image_url} alt="" data-testid="reward-image-preview" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon size={22} className="text-slate-300" />
                  )}
                </div>
                <div className="flex-1">
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload}
                    data-testid="reward-image-input" className="hidden" />
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    data-testid="reward-image-upload-btn"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-xs font-medium text-[#1A1A1A] hover:border-[#D44E20]/40 disabled:opacity-50">
                    {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    {form.image_url ? 'Sostituisci' : 'Carica immagine'}
                  </button>
                  {form.image_url && (
                    <button type="button" onClick={() => setForm(f => ({ ...f, image_url: '' }))}
                      data-testid="reward-image-remove-btn"
                      className="ml-2 text-xs text-[#EF4444] hover:underline">Rimuovi</button>
                  )}
                  <p className="text-[10px] text-[#94A3B8] mt-1">JPG/PNG/WebP, max 10MB</p>
                </div>
              </div>
            </div>
            <div className="mb-4">
              <label className="text-[#64748B] text-xs font-medium block mb-1.5">Emoji {form.image_url && <span className="text-[10px] text-[#94A3B8]">(non usato — c'e l'immagine)</span>}</label>
              <div className="flex flex-wrap gap-2">{EMOJIS.map(e => <button key={e} onClick={() => setForm(f => ({ ...f, emoji: e }))} className={`text-2xl w-10 h-10 rounded-xl transition-all ${form.emoji === e ? 'bg-[#D44E20]/20 ring-2 ring-[#D44E20]' : 'bg-gray-50 hover:bg-slate-100'}`}>{e}</button>)}</div>
            </div>
            {[['Nome *', 'name', 'text', 'Caffè espresso gratis'], ['Descrizione', 'description', 'text', 'Un caffè offerto dalla casa'], ['Costo punti *', 'points_cost', 'number', '50']].map(([label, key, type, ph]) => (
              <div key={key} className="mb-3">
                <label className="text-[#64748B] text-xs font-medium block mb-1">{label}</label>
                <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={ph} className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
              </div>
            ))}
            <div className="mb-3">
              <label className="text-[#64748B] text-xs font-medium block mb-1">Tier minimo</label>
              <select value={form.min_tier} onChange={e => setForm(f => ({ ...f, min_tier: e.target.value }))} className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none">
                {TIERS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
            {/* Allowed days */}
            <div className="mb-3">
              <label className="text-[#64748B] text-xs font-medium block mb-1.5">Giorni disponibili (vuoto = tutti)</label>
              <div className="flex gap-1.5">
                {DAYS.map(d => (
                  <button key={d.value} onClick={() => toggleDay(d.value)} data-testid={`day-${d.value}`}
                    className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all ${
                      form.allowed_days.includes(d.value) ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-gray-50 text-[#64748B] border-slate-200'
                    }`}>{d.label}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><label className="text-[#64748B] text-xs font-medium block mb-1">Dalle ore (opz.)</label><input type="number" min="0" max="23" value={form.available_from_hour} onChange={e => setForm(f => ({ ...f, available_from_hour: e.target.value }))} placeholder="7" className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" /></div>
              <div><label className="text-[#64748B] text-xs font-medium block mb-1">Alle ore (opz.)</label><input type="number" min="0" max="23" value={form.available_to_hour} onChange={e => setForm(f => ({ ...f, available_to_hour: e.target.value }))} placeholder="11" className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" /></div>
            </div>
            <div className="mb-4"><label className="text-[#64748B] text-xs font-medium block mb-1">Limite giornaliero (opz.)</label><input type="number" value={form.daily_limit} onChange={e => setForm(f => ({ ...f, daily_limit: e.target.value }))} placeholder="10" className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" /></div>
            {/* Reward-specific T&C — shown on the per-reward detail page. */}
            <div className="mb-4">
              <label className="text-[#64748B] text-xs font-medium block mb-1">Termini & condizioni (opz.)</label>
              <textarea value={form.terms} onChange={e => setForm(f => ({ ...f, terms: e.target.value }))}
                placeholder={'Esempio:\n- Riscattabile una volta al giorno\n- Non cumulabile con altre offerte\n- Solo per consumazione al banco'}
                rows={4}
                data-testid="reward-terms-textarea"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none resize-y" />
              <p className="text-[10px] text-[#94A3B8] mt-1">Verra mostrato nella pagina dettaglio del premio nella bacheca cliente.</p>
            </div>
            <div className="mb-4"><ScopeSelector value={scope} onChange={setScope} /></div>
            <div className="flex gap-3">
              <button onClick={() => setShowForm(false)} className="flex-1 h-11 bg-slate-100 text-[#64748B] rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors">Annulla</button>
              <button onClick={handleSave} disabled={saving} data-testid="save-reward-btn" className="flex-1 h-11 bg-[#D44E20] text-white rounded-xl text-sm font-semibold hover:bg-[#B83D15] disabled:opacity-50 transition-colors">{saving ? 'Salvataggio...' : 'Salva'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ── BENEFITS TAB ─────────────────────────────────────────────── */
function BenefitsTab() {
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_BENEFIT);
  const [saving, setSaving] = useState(false);

  const fetchBenefits = () => { adminApi.get('/tier-benefits').then(r => setBenefits(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { fetchBenefits(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY_BENEFIT); setShowForm(true); };
  const openEdit = (b) => {
    setEditing(b.id);
    const freq = b.frequency || '1/week';
    const [fc, fp] = freq.includes('/') ? freq.split('/') : ['1', 'week'];
    setForm({ tier: b.tier, benefit_type: b.benefit_type, discount_pct: b.discount_pct ?? '', item_name: b.item_name || '', item_emoji: b.item_emoji || '🌮', freq_count: fc, freq_period: fp, allowed_days: b.allowed_days || [], description: b.description || '', is_active: b.is_active });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (form.benefit_type === 'discount' && !form.discount_pct) { toast.error('Specifica la percentuale di sconto'); return; }
    if (form.benefit_type === 'free_item' && !form.item_name) { toast.error('Specifica il nome dell\'articolo'); return; }
    setSaving(true);
    const frequency = form.benefit_type === 'free_item' ? `${form.freq_count || 1}/${form.freq_period || 'week'}` : null;
    const payload = {
      tier: form.tier, benefit_type: form.benefit_type, description: form.description, is_active: form.is_active,
      discount_pct: form.benefit_type === 'discount' ? parseFloat(form.discount_pct) : null,
      item_name: form.benefit_type === 'free_item' ? form.item_name : null,
      item_emoji: form.benefit_type === 'free_item' ? form.item_emoji : null,
      frequency: frequency,
      allowed_days: form.allowed_days.length > 0 ? form.allowed_days : null,
    };
    try {
      if (editing) { await adminApi.put(`/tier-benefits/${editing}`, payload); toast.success('Beneficio aggiornato'); }
      else { await adminApi.post('/tier-benefits', payload); toast.success('Beneficio creato'); }
      setShowForm(false); fetchBenefits();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (b) => { await adminApi.put(`/tier-benefits/${b.id}`, { is_active: !b.is_active }); fetchBenefits(); };
  const handleDelete = async (id) => { if (!window.confirm('Eliminare questo beneficio?')) return; await adminApi.delete(`/tier-benefits/${id}`); toast.success('Beneficio eliminato'); fetchBenefits(); };

  const tierColor = { bronze: '#CD7F32', silver: '#C0C0C0', gold: '#FFD700', platinum: '#888' };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[#64748B] text-sm">{benefits.length} vantaggi configurati</p>
          <p className="text-[#64748B] text-xs mt-0.5">I vantaggi sono automaticamente attivi per i clienti nel tier corrispondente e non costano punti.</p>
        </div>
        <button onClick={openAdd} data-testid="add-benefit-btn" className="flex items-center gap-2 px-4 py-2.5 bg-[#D44E20] text-white rounded-xl text-sm font-semibold hover:bg-[#B83D15] transition-colors">
          <Plus size={16} /> Aggiungi Vantaggio
        </button>
      </div>
      {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div> : (
        <>
          {TIERS.filter(t => t !== 'bronze').map(tier => {
            const tierBenefits = benefits.filter(b => b.tier === tier);
            if (tierBenefits.length === 0) return null;
            return (
              <div key={tier} className="mb-6">
                <PageTooltip page="premi" />
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tierColor[tier] }} />
                  <h3 className="font-semibold text-[#1A1A1A] capitalize">{tier}</h3>
                  <span className="text-xs text-[#64748B]">({tierBenefits.length} vantaggi)</span>
                </div>
                <div className="space-y-2">
                  {tierBenefits.map(b => (
                    <div key={b.id} data-testid={`benefit-item-${b.id}`} className={`bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex items-center gap-4 ${!b.is_active ? 'opacity-50' : ''}`}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl" style={{ backgroundColor: `${tierColor[b.tier]}20` }}>
                        {b.benefit_type === 'discount' ? '🏷️' : (b.item_emoji || '🎁')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#1A1A1A] font-semibold text-sm">
                          {b.benefit_type === 'discount' ? `Sconto ${b.discount_pct}%` : b.item_name}
                        </p>
                        <p className="text-[#64748B] text-xs">{b.description}</p>
                        {b.benefit_type === 'free_item' && b.frequency && (
                          <span className="text-blue-600 text-xs bg-blue-50 px-1.5 py-0.5 rounded-full mt-1 inline-block">{b.frequency}</span>
                        )}
                        {b.allowed_days && b.allowed_days.length > 0 && b.allowed_days.length < 7 && (
                          <span className="text-purple-600 text-xs bg-purple-50 px-1.5 py-0.5 rounded-full mt-1 inline-block ml-1">
                            {b.allowed_days.sort((a,c) => a-c).map(d => DAYS.find(x => x.value === d)?.label).join(', ')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleToggle(b)} className="text-[#64748B] hover:text-[#D44E20]">
                          {b.is_active ? <ToggleRight size={22} className="text-[#3ECF8E]" /> : <ToggleLeft size={22} />}
                        </button>
                        <button onClick={() => openEdit(b)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-50 text-[#64748B] hover:bg-slate-100"><Pencil size={14} /></button>
                        <button onClick={() => handleDelete(b.id)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-[#EF4444] hover:bg-red-100"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {benefits.length === 0 && (
            <div className="text-center py-16">
              <Shield size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-[#64748B] text-sm">Nessun vantaggio tier configurato</p>
              <p className="text-[#64748B] text-xs mt-1">Aggiungi sconti o articoli gratuiti per i clienti dei tier superiori</p>
            </div>
          )}
        </>
      )}
      {/* Benefits Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-[#1A1A1A] text-lg">{editing ? 'Modifica Vantaggio' : 'Nuovo Vantaggio Tier'}</h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-[#64748B] hover:bg-gray-200"><X size={16} /></button>
            </div>
            {/* Tier */}
            <div className="mb-4">
              <label className="text-[#64748B] text-xs font-medium block mb-1.5">Tier</label>
              <div className="flex gap-2">
                {TIERS.filter(t => t !== 'bronze').map(t => (
                  <button key={t} onClick={() => setForm(f => ({ ...f, tier: t }))}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-medium border transition-all capitalize ${
                      form.tier === t ? 'text-white border-[#D44E20]' : 'bg-white text-[#64748B] border-slate-200'
                    }`} style={form.tier === t ? { backgroundColor: tierColor[t] } : {}}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            {/* Type */}
            <div className="mb-4">
              <label className="text-[#64748B] text-xs font-medium block mb-1.5">Tipo di vantaggio</label>
              <div className="flex gap-2">
                <button onClick={() => setForm(f => ({ ...f, benefit_type: 'discount' }))}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-medium border transition-all ${form.benefit_type === 'discount' ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-white text-[#64748B] border-slate-200'}`}>
                  Sconto %
                </button>
                <button onClick={() => setForm(f => ({ ...f, benefit_type: 'free_item' }))}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-medium border transition-all ${form.benefit_type === 'free_item' ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-white text-[#64748B] border-slate-200'}`}>
                  Articolo gratuito
                </button>
              </div>
            </div>
            {form.benefit_type === 'discount' ? (
              <>
              <div className="mb-3">
                <label className="text-[#64748B] text-xs font-medium block mb-1">Percentuale sconto *</label>
                <div className="flex items-center gap-2">
                  <input type="number" min="1" max="100" value={form.discount_pct} onChange={e => setForm(f => ({ ...f, discount_pct: e.target.value }))} placeholder="10" data-testid="benefit-discount-input"
                    className="w-24 h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] text-center font-bold focus:border-[#D44E20] focus:outline-none" />
                  <span className="text-[#64748B] text-sm font-medium">%</span>
                </div>
              </div>
              {/* Allowed days for discount */}
              <div className="mb-3">
                <label className="text-[#64748B] text-xs font-medium block mb-1.5">Giorni disponibili (vuoto = tutti)</label>
                <div className="flex gap-1.5">
                  {DAYS.map(d => (
                    <button key={d.value} onClick={() => setForm(f => ({ ...f, allowed_days: f.allowed_days.includes(d.value) ? f.allowed_days.filter(x => x !== d.value) : [...f.allowed_days, d.value] }))}
                      data-testid={`benefit-discount-day-${d.value}`}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all ${
                        form.allowed_days.includes(d.value) ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-gray-50 text-[#64748B] border-slate-200'
                      }`}>{d.label}</button>
                  ))}
                </div>
              </div>
              </>
            ) : (
              <>
                <div className="mb-3">
                  <label className="text-[#64748B] text-xs font-medium block mb-1.5">Emoji articolo</label>
                  <div className="flex flex-wrap gap-2">{EMOJIS.map(e => <button key={e} onClick={() => setForm(f => ({ ...f, item_emoji: e }))} className={`text-2xl w-10 h-10 rounded-xl transition-all ${form.item_emoji === e ? 'bg-[#D44E20]/20 ring-2 ring-[#D44E20]' : 'bg-gray-50 hover:bg-slate-100'}`}>{e}</button>)}</div>
                </div>
                <div className="mb-3">
                  <label className="text-[#64748B] text-xs font-medium block mb-1">Nome articolo *</label>
                  <input type="text" value={form.item_name} onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))} placeholder="Caffè espresso" data-testid="benefit-item-name"
                    className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                </div>
                <div className="mb-3">
                  <label className="text-[#64748B] text-xs font-medium block mb-1">Frequenza</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min="1" max="99" value={form.freq_count} onChange={e => setForm(f => ({ ...f, freq_count: e.target.value }))} data-testid="benefit-freq-count"
                      className="w-16 h-10 bg-slate-50 border border-slate-200 rounded-xl px-2 text-sm text-[#1A1A1A] text-center font-bold focus:border-[#D44E20] focus:outline-none" />
                    <span className="text-[#64748B] text-sm">per</span>
                    <select value={form.freq_period} onChange={e => setForm(f => ({ ...f, freq_period: e.target.value }))} data-testid="benefit-freq-period"
                      className="flex-1 h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none">
                      {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                </div>
                {/* Allowed days for benefit */}
                <div className="mb-3">
                  <label className="text-[#64748B] text-xs font-medium block mb-1.5">Giorni disponibili (vuoto = tutti)</label>
                  <div className="flex gap-1.5">
                    {DAYS.map(d => (
                      <button key={d.value} onClick={() => setForm(f => ({ ...f, allowed_days: f.allowed_days.includes(d.value) ? f.allowed_days.filter(x => x !== d.value) : [...f.allowed_days, d.value] }))}
                        data-testid={`benefit-day-${d.value}`}
                        className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all ${
                          form.allowed_days.includes(d.value) ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-gray-50 text-[#64748B] border-slate-200'
                        }`}>{d.label}</button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div className="mb-4">
              <label className="text-[#64748B] text-xs font-medium block mb-1">Descrizione (opz.)</label>
              <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Es. Sconto non cumulabile" data-testid="benefit-description"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowForm(false)} className="flex-1 h-11 bg-slate-100 text-[#64748B] rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors">Annulla</button>
              <button onClick={handleSave} disabled={saving} data-testid="save-benefit-btn" className="flex-1 h-11 bg-[#D44E20] text-white rounded-xl text-sm font-semibold hover:bg-[#B83D15] disabled:opacity-50 transition-colors">{saving ? 'Salvataggio...' : 'Salva'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
